const STORAGE_KEY = "jari:dev-access-override";

export type DevAccessOverride = "paid" | "free" | "license" | "guest";

type DevAccessState = DevAccessOverride | null;

const CYCLE: DevAccessOverride[] = ["paid", "free", "guest", "license"];

const listeners = new Set<() => void>();

function readFromStorage(): DevAccessState {
  if (!isDevAccessEnabled()) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "paid" || raw === "free" || raw === "license" || raw === "guest") return raw;
  } catch {
    /* ignore */
  }
  return null;
}

function writeToStorage(mode: DevAccessState): void {
  try {
    if (mode === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, mode);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

let current: DevAccessState = readFromStorage();

function emit(): void {
  for (const listener of listeners) listener();
}

export function isDevAccessEnabled(): boolean {
  return import.meta.env.DEV;
}

export function getDevAccessOverride(): DevAccessState {
  return current;
}

export function setDevAccessOverride(mode: DevAccessState): void {
  if (!isDevAccessEnabled()) return;
  current = mode;
  writeToStorage(mode);
  emit();
}

export function cycleDevAccessOverride(): void {
  if (!isDevAccessEnabled()) return;
  if (current === null) {
    setDevAccessOverride("paid");
    return;
  }
  const idx = CYCLE.indexOf(current);
  const next = CYCLE[(idx + 1) % CYCLE.length];
  setDevAccessOverride(next);
}

export function clearDevAccessOverride(): void {
  setDevAccessOverride(null);
}

export function subscribeDevAccessOverride(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getDevAccessOverrideSnapshot(): DevAccessState {
  return current;
}

/** Popup delay for guest access prompt: 15s in dev, 5 min in production. */
export function getGuestPromptDelayMs(): number {
  return isDevAccessEnabled() ? 15_000 : 5 * 60 * 1000;
}
